import React, { useState, useLayoutEffect } from 'react';
import { Modal, Select } from '@gerdau/hefesto/';
import { ModalText } from './CartProductTable/style';
import {
  CartOptions,
  NewCartItem,
  PaymentTerm
} from '../../../../services/MVP/models';
import { paymentAnticipated } from '../../../../services/MVP/utils';
import { DateType } from '@gerdau/hefesto-mais-web/DateField/DateField.types';
import { OptionsProps } from './CartProductTable';

// Workaround para suprir bug do componente Select da biblioteca Hefesto.
// Card GR-1803
export const useBodyScrollLock = ({ isLocked }: { isLocked: boolean }) => {
  useLayoutEffect(() => {
    if (!isLocked) return;

    const scrollBarWidth =
      window.innerWidth - document.documentElement.clientWidth;

    const originalOverflow = document.body.style.overflow;
    const originalPaddingRight = document.body.style.paddingRight;

    document.body.style.overflow = 'hidden';

    if (scrollBarWidth > 0) {
      document.body.style.paddingRight = `${scrollBarWidth}px`;
    }

    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.paddingRight = originalPaddingRight;
    };
  }, [isLocked]);
};

export const ChangePaymentMethodModal = ({
  show,
  title,
  description,
  allItems = [],
  handleNextBtn,
  handleNextBtnTitle,
  handleCloseValidationModal,
  loading,
  hiddenLeftButton,
  hasCloseBtn,
  isBlockedCustomer,
  hasSomeAgreement
}: {
  hiddenLeftButton?: boolean;
  loading?: boolean;
  hasCloseBtn?: boolean;
  isBlockedCustomer?: boolean;
  show: boolean;
  title: string;
  description: string;
  allItems: NewCartItem[];
  handleNextBtnTitle?: string;
  handleNextBtn: ({
    date,
    payment,
    fiscalBenefits,
    destination,
    updatePrice
  }: OptionsProps) => void;
  handleCloseValidationModal: () => void;
  hasSomeAgreement: (inptValue: string | DateType) => {
    date: boolean;
    destination: boolean;
    payment: boolean;
    fiscalBenefit: boolean;
  };
}) => {
  const [optionsSelect, setOptionsSelect] = useState('');
  const [canShowAgreementModal, setCanShowAgreementModal] = useState(false);

  useBodyScrollLock({ isLocked: show });

  if (!show || loading) {
    return null;
  }

  const paymentsTermAllowed = allItems
    .map((item) => {
      const destination = item.extension_attributes.destination_gerdau;
      return item.cartOptions[destination];
    })
    .filter(Boolean)
    .map((opt: CartOptions) => opt.paymentTerms)
    .reduce((acc: PaymentTerm[], item: PaymentTerm[], idx: number) => {
      if (idx === 0) return item;
      return acc.filter((accOption) =>
        item.some((currentOption) => currentOption.term === accOption.term)
      );
    }, []);

  const anticipatedTermsAvailable: PaymentTerm[] = isBlockedCustomer
    ? allItems
        .flatMap((item) => {
          const destination = item.extension_attributes.destination_gerdau;
          return item.cartOptions[destination]?.paymentTerms ?? [];
        })
        .filter((pt) => paymentAnticipated.includes(pt.term))
        .reduce(
          (acc: PaymentTerm[], pt) =>
            acc.some((a) => a.term === pt.term) ? acc : [...acc, pt],
          []
        )
    : [];

  const paymentsTerm = isBlockedCustomer
    ? anticipatedTermsAvailable
    : paymentsTermAllowed;

  const paymentsTermOpt = paymentsTerm.map((payment) => ({
    value: payment.term,
    label: payment.description || payment.term
  }));

  const handleMiddleware = ({ value }: { value: string | DateType }) => {
    const hasAgreementItems = hasSomeAgreement(value);

    if (hasAgreementItems.payment) {
      setCanShowAgreementModal(true);
    }
  };

  if (canShowAgreementModal) {
    return (
      <Modal
        buttons={{
          left: {
            children: 'Cancelar',
            name: 'cancel-delete',
            type: 'button',
            variant: 'secondary',
            onClick: () => {
              setCanShowAgreementModal(false);
            },
            disabled: loading,
            loading
          },
          right: {
            children: 'Continuar',
            name: 'continue',
            type: 'button',
            variant: 'primary',
            onClick: () => {
              handleNextBtn({
                payment: String(optionsSelect),
                closeWhenSucess: true
              });
              setCanShowAgreementModal(false);
            },
            disabled: loading,
            loading
          }
        }}
        modalSize="sm"
        title="Alterar a condição de pagamento afetará o preço acordado"
      >
        <ModalText>
          Será necessário recalcular o preço após essa alteração. Deseja alterar
          a condição de pagamento?
        </ModalText>
      </Modal>
    );
  }

  return (
    <Modal
      buttons={{
        ...(!hiddenLeftButton && {
          left: {
            children: 'Cancelar',
            name: 'cancel-delete',
            type: 'button',
            variant: 'secondary',
            onClick: handleCloseValidationModal,
            disabled: loading,
            loading
          }
        }),
        right: {
          children: handleNextBtnTitle ? handleNextBtnTitle : 'Alterar',
          name: 'change',
          type: 'button',
          variant: 'primary',
          onClick: () =>
            handleNextBtn({
              payment: String(optionsSelect),
              closeWhenSucess: true
            }),
          disabled: loading || !optionsSelect,
          loading
        }
      }}
      modalSize="sm"
      title={title}
      {...(hasCloseBtn && { onClose: handleCloseValidationModal })}
    >
      <ModalText>{description}</ModalText>

      <Select
        options={paymentsTermOpt}
        onSelect={(e: string | string[]) => {
          if (Array.isArray(e)) {
            setOptionsSelect(String(e[0]));
            return handleMiddleware({ value: e[0] });
          }
          setOptionsSelect(String(e));
          handleMiddleware({ value: e });
        }}
        label="Condição de pagamento"
        name="payment"
        value={optionsSelect}
        disabled={loading}
      />
    </Modal>
  );
};
